import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, Type } from '@angular/core';
import { EMPTY, from, Observable, of, Subject } from 'rxjs';
import { Ticket, EnhancedFareEngineTicket, PreActivationCheckResponse } from '../models/ticket.model';
import { NgRedux } from '@angular-redux/store';
import { DeviceIdService } from '@ridango/core/lib/services';
import { finalize, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { Temporal } from '@js-temporal/polyfill';
import { Capacitor } from '@capacitor/core';
import { FareContractStatus, FareContractToTicketsStatusMap, TicketStatus } from '../models/ticket-status.model';
import { AccountTicketsService as _AccountTicketsService } from '../../account-tickets/account-tickets.service';
import { ModalController } from '@ionic/angular';
import { TicketsService } from '../../tickets/tickets.service';
import { getPurchaseChannel } from '@ridango/core/lib/utils/channel.util';
import { PurchaseChannel } from '@ridango/core/lib/enums/purchase-channel.enum';

@Injectable({
    providedIn: 'root'
})
export class AccountTicketsService {
    public declineAnonymousUserRequest$ = new Subject();
    public declineAuthorizedUserRequest$ = new Subject();

    constructor(
        @Inject('ENV') private env,
        private http: HttpClient,
        private store: NgRedux<any>,
        private deviceIdService: DeviceIdService,
        private accountTicketsService: _AccountTicketsService,
        private modalCtrl: ModalController,
        private ticketsService: TicketsService
    ) {}

    declineAnonymousUserSubject() {
        this.declineAnonymousUserRequest$.next();
        this.declineAnonymousUserRequest$.complete();
        this.declineAnonymousUserRequest$ = new Subject();
    }

    declineAuthorizedUserSubject() {
        this.declineAuthorizedUserRequest$.next();
        this.declineAuthorizedUserRequest$.complete();
        this.declineAuthorizedUserRequest$ = new Subject();
    }

    fetchAccountTickets(
        params?: {
            page?: number;
            limit?: number;
            expiredHours?: number;
            expiredMinutes?: number;
            sort?: 'asc' | 'desc';
        },
        deviceTokenOnly?: boolean
    ): Observable<Ticket[]> {
        const channel: PurchaseChannel = getPurchaseChannel(this.env);
        const channelParams = { channel: [channel] };
        const options: {
            responseType: 'text';
            params: {
                page?: number;
                limit?: number;
                expiredHours?: number;
                expiredMinutes?: number;
                sort?: 'asc' | 'desc';
                channel?: string[];
            };
            headers?: HttpHeaders;
        } = {
            responseType: 'text',
            params: channelParams
        };

        if (params && Object.keys(params).length) {
            options.params = {
                ...options.params,
                ...params
            };
        }

        if (!params?.sort && this.env.TICKET_SORT_ORDER) {
            options.params.sort = this.env.TICKET_SORT_ORDER;
        }

        let url: string;

        if (deviceTokenOnly && this.deviceIdService.getAccessDeviceToken()) {
            const token = this.deviceIdService.getAccessDeviceToken();
            options.params['deviceTokenOnly'] = true;
            options.headers = new HttpHeaders({
                Authorization: `Bearer ${token}`
            });
        }

        if (options.params['page'] !== undefined) {
            options.params['offset'] = options.params['page'];
        }

        if (this.env.IS_NEW_AFC || this.env.WMB?.WMB_PUBLIC_API) {
            url = `${this.env.WMB.WMB_PUBLIC_API}/v1/ticket`;
        } else {
            url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/ticket/active`;
        }

        const takeUntilSubject$ = deviceTokenOnly
            ? this.declineAnonymousUserRequest$
            : this.declineAuthorizedUserRequest$;
        return this.http.get(url, options).pipe(
            takeUntil(takeUntilSubject$),
            map((json) => {
                const modifiedJson = json.replace(/("docNum":\s*)(\d+)(?=\s*[,}])/g, '$1"$2"');
                return JSON.parse(modifiedJson);
            }),
            switchMap((tickets: Ticket[]) => {
                const ticketListV2Map = (tickets: Ticket[]) => {
                    return tickets.map((ticket: EnhancedFareEngineTicket) => ({
                        ...ticket,
                        expiredAt:
                            ticket.status === FareContractStatus.EXPIRED ||
                            ticket.status === FareContractStatus.CANCELLED
                                ? ticket.endDate
                                : null,
                        status: FareContractToTicketsStatusMap.get(ticket.status as FareContractStatus) || '',
                        validated: ticket.status === FareContractStatus.ACTIVE,
                        activated: ticket.status === FareContractStatus.ACTIVE,
                        starting: ticket.status === FareContractStatus.ACTIVE ? ticket.startDate : undefined,
                        ending: ticket.status === FareContractStatus.ACTIVE ? ticket.endDate : undefined
                    }));
                };
                if (this.env.TICKET_LIST_V2_ENABLED) {
                    tickets = ticketListV2Map(tickets);
                }

                return of(tickets);
            }),
            finalize(() => {
                if (deviceTokenOnly) {
                    this.declineAnonymousUserSubject();
                } else {
                    this.declineAuthorizedUserSubject();
                }
            })
        );
    }

    fetchAccountTicketById(id: number | string): Observable<Ticket> {
        let url: string;
        if (this.env.IS_NEW_AFC) {
            url = `${this.env.WMB.WMB_PUBLIC_API}/v1/ticket/${id}`;
        } else {
            url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/ticket/${id}`;
        }
        return this.http.get<Ticket>(url);
    }

    preActivateTicketCheck(ticketId: number | string): Observable<PreActivationCheckResponse> {
        let url: string;
        const nowInSeconds = Math.floor(Date.now() / 1000);
        if (this.env.IS_NEW_AFC) {
            url = `${this.env.WMB.WMB_PUBLIC_API}/v1/ticket/${ticketId}/pre-activate-check?dateTime=${nowInSeconds}`;
        } else {
            url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/ticket/${ticketId}/pre-activate-check?dateTime=${nowInSeconds}`;
        }
        return this.http.get<PreActivationCheckResponse>(url);
    }

    fetchAccountTicketsHistory(
        formattedFromDate: string,
        formattedToDate: string,
        includeReturnedTickets: boolean
    ): Observable<Ticket[]> {
        const channel: PurchaseChannel = getPurchaseChannel(this.env);
        let url: string;
        if (this.env.IS_NEW_AFC || this.env.WMB?.WMB_PUBLIC_API) {
            url = `${this.env.WMB.WMB_PUBLIC_API}/v1/ticket/history`;
        } else {
            url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/ticket/history`;
        }
        return this.http.get<Ticket[]>(url, {
            params: {
                startDateStr: formattedFromDate,
                endDateStr: formattedToDate,
                includeReturnedTickets: includeReturnedTickets,
                includeCancelledTickets: !!this.env.SHOW_TICKET_HISTORY_CANCELLED,
                channel: [channel]
            }
        });
    }

    activateTicket(docNum: string): Observable<Ticket> {
        let url: string;
        if (this.env.APP_NAME === 'orebro' || this.env.APP_NAME === 'varmland') {
            url = `${this.env.WMB.WMB_PUBLIC_API}/v1/ticket/${docNum}/activate`;
        } else {
            url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/ticket/${docNum}/activate`;
        }
        return this.http.put<Ticket>(url, null);
    }

    public transferAnonymousData(modalComponent: Type<any>): Observable<void> {
        return this.fetchAccountTickets({ page: 0, limit: 50 }, true).pipe(
            take(1),
            switchMap((tickets) => this.showTransferAnonymousModal(tickets, modalComponent))
        );
    }

    private showTransferAnonymousModal(tickets: Ticket[], modalComponent: Type<any>): Observable<void> {
        if (!tickets.length) {
            return EMPTY;
        }

        const modalId = 'transferAnonymousModal';
        const modal = this.modalCtrl.create({
            component: modalComponent,
            cssClass: 'transfer-anonymous-modal',
            componentProps: { modalId, tickets },
            id: modalId
        });
        return from(modal).pipe(
            switchMap((modal) => from(modal.present())),
            map(() => void 0)
        );
    }

    showAztecCode(ticket: Ticket): boolean {
        if (!Capacitor.isNativePlatform()) {
            return false;
        }

        const { mtb, loaned, isOwner } = ticket;

        const isExpiredTicket =
            (ticket.ending &&
                Temporal.Instant.from(ticket.ending?.toString()).epochMilliseconds <
                    Temporal.Now.instant().epochMilliseconds &&
                (ticket.initialUnits === null || ticket.initialUnits === 1 || ticket.units === 0)) ||
            (ticket.bookletEnding &&
                Temporal.Instant.from(ticket.bookletEnding?.toString()).epochMilliseconds <
                    Temporal.Now.instant().epochMilliseconds);

        if (this.env.TICKET_SHOW_AZTEC_LOANED) {
            if (
                (!this.env.IS_NEW_AFC && this.env.HISTORY_TICKET_HIDE_QR_CODE && isExpiredTicket) ||
                this.isTicketRefunded(ticket)
            ) {
                return false;
            }
            return !!mtb && (!loaned || !isOwner) && !this.isTicketRefunded(ticket);
        }

        return !!mtb && !loaned && !this.isTicketRefunded(ticket);
    }

    private isTicketRefunded(ticket): boolean {
        return ticket.status === TicketStatus.Returned;
    }
}
